import { differenceInCalendarDays, parseISO, startOfToday } from 'date-fns';
import { z } from 'zod';

import type { CampaignRestrictions } from '../../types';

export interface SchemaRestrictions {
  minLeadDays: number;
  minDurationDays: number;
  maxDurationDays: number;
  minClippingBudget: number;
  minInfluencerBudget: number;
}

const DEFAULT_RESTRICTIONS: SchemaRestrictions = {
  minLeadDays: 3,
  minDurationDays: 3,
  maxDurationDays: 10,
  minClippingBudget: 500,
  minInfluencerBudget: 2000,
};

export function restrictionsToSchema(
  restrictions: CampaignRestrictions,
): SchemaRestrictions {
  return {
    minLeadDays: restrictions.clipping.min_lead_time_in_days,
    minDurationDays: restrictions.clipping.min_duration_in_days,
    maxDurationDays: restrictions.clipping.max_duration_in_days,
    minClippingBudget: restrictions.clipping.min_budget / 100,
    minInfluencerBudget: restrictions.influencer.min_budget / 100,
  };
}

const baseSchema = z.object({
  campaignStartDate: z
    .string()
    .min(1, 'Start date is required')
    .pipe(z.iso.date('Must be a valid date (YYYY-MM-DD)')),
  campaignEndDate: z
    .string()
    .min(1, 'End date is required')
    .pipe(z.iso.date('Must be a valid date (YYYY-MM-DD)')),
  advertiser: z.string().min(1, 'Advertiser is required'),
  artist: z
    .string()
    .transform((v) => v.trim())
    .pipe(z.string().min(1, 'Artist is required')),
  track: z
    .string()
    .transform((v) => v.trim())
    .pipe(z.string().min(1, 'Track is required')),
  isrc: z
    .string()
    .transform((v) => v.trim().toUpperCase())
    .pipe(
      z
        .string()
        .min(1, 'ISRC is required')
        .regex(/^[A-Z]{2}[A-Z0-9]{3}\d{7}$/, 'Must be a valid ISRC format'),
    ),
  spotifyUrl: z
    .string()
    .min(1, 'Spotify URL is required')
    .pipe(z.url('Must be a valid URL')),
  tiktokLink: z
    .string()
    .transform((v) => v.trim())
    .pipe(z.union([z.literal(''), z.url('Must be a valid URL')])),
  instagramLink: z
    .string()
    .transform((v) => v.trim())
    .pipe(z.union([z.literal(''), z.url('Must be a valid URL')])),
  clippingEnabled: z.boolean(),
  clippingBudget: z.number().optional(),
  clippingPurchaseOrder: z.string().optional(),
  influencerEnabled: z.boolean(),
  influencerBudget: z.number().optional(),
  influencerPurchaseOrder: z.string().optional(),
});

export function createCampaignSchema(
  restrictions: SchemaRestrictions = DEFAULT_RESTRICTIONS,
) {
  const {
    minLeadDays,
    minDurationDays,
    maxDurationDays,
    minClippingBudget,
    minInfluencerBudget,
  } = restrictions;

  return baseSchema.superRefine((data, ctx) => {
    if (data.clippingEnabled) {
      if (!data.clippingBudget || data.clippingBudget < minClippingBudget) {
        ctx.addIssue({
          code: 'custom',
          message: `Minimum spend is $${minClippingBudget.toLocaleString()}`,
          path: ['clippingBudget'],
        });
      }

      if (!data.clippingPurchaseOrder?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'Purchase order is required',
          path: ['clippingPurchaseOrder'],
        });
      }
    }

    if (data.influencerEnabled) {
      if (
        !data.influencerBudget ||
        data.influencerBudget < minInfluencerBudget
      ) {
        ctx.addIssue({
          code: 'custom',
          message: `Minimum spend is $${minInfluencerBudget.toLocaleString()}`,
          path: ['influencerBudget'],
        });
      }

      if (!data.influencerPurchaseOrder?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'Purchase order is required',
          path: ['influencerPurchaseOrder'],
        });
      }
    }

    const hasChannel = data.clippingEnabled || data.influencerEnabled;

    if (!hasChannel) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one campaign channel must be enabled',
        path: ['clippingEnabled'],
      });

      return;
    }

    if (!data.campaignStartDate || !data.campaignEndDate) {
      return;
    }

    const today = startOfToday();
    const start = parseISO(data.campaignStartDate);
    const end = parseISO(data.campaignEndDate);

    const leadDays = differenceInCalendarDays(start, today);

    if (leadDays < minLeadDays) {
      ctx.addIssue({
        code: 'custom',
        message: `Start date must be at least ${minLeadDays} days from today`,
        path: ['campaignStartDate'],
      });
    }

    const duration = differenceInCalendarDays(end, start);

    if (duration < minDurationDays) {
      ctx.addIssue({
        code: 'custom',
        message: `Campaign must be at least ${minDurationDays} days long`,
        path: ['campaignEndDate'],
      });
    }

    if (duration > maxDurationDays) {
      ctx.addIssue({
        code: 'custom',
        message: `Campaign cannot exceed ${maxDurationDays} days`,
        path: ['campaignEndDate'],
      });
    }
  });
}

export type CreateCampaignFormValues = z.infer<
  ReturnType<typeof createCampaignSchema>
>;
